import { getStoredUtmParams, parseUtmParamsFromSearch } from './utm';

type Touch = Record<string, string>;
const FIRST_TOUCH_KEY = 'bookstera_first_touch_v1';
const SESSION_TOUCH_KEY = 'bookstera_session_touch_v1';
const AUTH_HOSTS = ['accounts.google.com', 'checkout.stripe.com', 'hooks.stripe.com'];
const SEARCH_HOST = /(^|\.)(google\.[a-z.]+|bing\.com|duckduckgo\.com|search\.yahoo\.com|search\.brave\.com)$/;
const SOCIAL_HOST = /(^|\.)(facebook\.com|instagram\.com|linkedin\.com|t\.co|twitter\.com|x\.com|tiktok\.com|pinterest\.[a-z.]+)$/;

export function classifyAcquisition(search: string, referrer: string, hostname: string): Touch | null {
  const utm = parseUtmParamsFromSearch(search);
  const params = new URLSearchParams(search);
  let host = '';
  try { host = new URL(referrer).hostname.toLowerCase(); } catch { /* no referrer */ }
  const medium = (utm.utm_medium || '').toLowerCase();
  const tagged = Object.keys(utm).length > 0;
  const ownHost = host.replace(/^www\./, '') === hostname.replace(/^www\./, '');
  if (!tagged && (ownHost || AUTH_HOSTS.includes(host))) return null;
  let channel = 'Direct';
  if (/^(paid_social|paidsocial|social_paid)$/.test(medium)) channel = 'Paid Social';
  else if (/^(cpc|ppc|paid|paid_search|display|cpm)$/.test(medium) || params.has('gclid') || params.has('msclkid')) channel = 'Paid';
  else if (/^(organic_social|social|social-organic)$/.test(medium)) channel = 'Organic Social';
  else if (medium === 'organic' || medium === 'organic_search') channel = 'Organic Search';
  else if (medium === 'email') channel = 'Email';
  else if (tagged) channel = 'Tagged Other';
  else if (SEARCH_HOST.test(host)) channel = 'Organic Search';
  // A Facebook referrer or fbclid cannot distinguish an ad from an organic post.
  else if (SOCIAL_HOST.test(host) || params.has('fbclid')) channel = 'Social Unclassified';
  else if (host) channel = 'Referral';
  return { ...utm, channel, source: utm.utm_source || host || 'direct', medium: utm.utm_medium || 'unclassified' };
}

function readTouch(storage: Storage, key: string): Touch | null {
  try { return JSON.parse(storage.getItem(key) || 'null'); } catch { return null; }
}

export function initializeAcquisition() {
  if (typeof window === 'undefined') return;
  try {
    const current = readTouch(sessionStorage, SESSION_TOUCH_KEY);
    const incoming = parseUtmParamsFromSearch(window.location.search);
    const newCampaign = Object.entries(incoming).some(([key, value]) => current?.[key] !== value);
    const recent = current && Date.now() - Date.parse(current.captured_at) < 30 * 60 * 1000;
    if (recent && !newCampaign) {
      sessionStorage.setItem(SESSION_TOUCH_KEY, JSON.stringify({ ...current, captured_at: new Date().toISOString() }));
      return;
    }
    const touch = classifyAcquisition(window.location.search, document.referrer, window.location.hostname);
    if (!touch) return;
    const next = { ...touch, landing_path: window.location.pathname, captured_at: new Date().toISOString() };
    sessionStorage.setItem(SESSION_TOUCH_KEY, JSON.stringify(next));
    if (!readTouch(localStorage, FIRST_TOUCH_KEY)) localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(next));
  } catch { /* Storage may be disabled; do not block checkout or authentication. */ }
}

export function getAcquisitionProperties(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  initializeAcquisition();
  try {
    const urlUtm = parseUtmParamsFromSearch(window.location.search);
    const first = readTouch(localStorage, FIRST_TOUCH_KEY);
    const session = readTouch(sessionStorage, SESSION_TOUCH_KEY);
    return {
      ...(Object.keys(urlUtm).length ? urlUtm : getStoredUtmParams() || {}),
      ...(session ? { acquisition_channel: session.channel, acquisition_source: session.source, acquisition_landing_path: session.landing_path } : {}),
      ...(first ? {
        first_touch_channel: first.channel,
        first_touch_source: first.source,
        first_touch_medium: first.medium,
        first_touch_campaign: first.utm_campaign || '',
        first_touch_content: first.utm_content || '',
        first_touch_landing_path: first.landing_path,
      } : {}),
    };
  } catch { return {}; }
}
